import React from "react";
import { ShieldAlert, ShieldCheck, ShieldQuestion, AlertTriangle, Bot, Cpu } from "lucide-react";

const STYLES = {
  block: { c: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered", l: "Blocked", I: ShieldAlert },
  hold: { c: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400", l: "Held", I: ShieldQuestion },
  allow_medium: { c: "border-status-info/40 bg-status-info/10 text-status-info", l: "Medium", I: ShieldCheck },
  allow_low: { c: "border-status-ok/40 bg-status-ok/10 text-status-ok", l: "Low", I: ShieldCheck },
};

export default function RiskPanel({ data }) {
  if (!data) return null;
  if (!data.available) {
    return (
      <div data-testid="risk-panel-empty" className="border border-border rounded-sm bg-card p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldQuestion className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Risk panel</div>
        </div>
        <div className="text-xs text-muted-foreground">
          No risk record on file for this transaction (likely a coinbase, treasury or pre-Phase-3 ledger entry).
        </div>
      </div>
    );
  }
  const v = data.verdict || {};
  const style = STYLES[v.decision] || STYLES.allow_low;
  const Icon = style.I;
  const isSender = data.role === "sender";

  return (
    <div data-testid="risk-panel" className="border border-border rounded-sm bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Risk score on receipt</div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{data.role}</div>
      </div>

      <div className="flex items-end gap-4">
        <div>
          <div className="text-4xl font-mono leading-none" data-testid="risk-final-score">
            {v.final_score ?? "?"}<span className="text-muted-foreground text-lg">/100</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">final score</div>
        </div>
        <div className={`px-2.5 py-1.5 border rounded-sm text-xs font-medium ${style.c}`} data-testid="risk-decision-pill">
          {style.l} · {v.decision}
        </div>
        {v.ai_offline && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-border bg-muted rounded-sm text-[10px] font-mono text-muted-foreground" title="LLM was offline; rule engine only">
            <Cpu className="w-3 h-3" strokeWidth={1.5} /> rule-only
          </span>
        )}
      </div>

      {isSender && (v.rule_score != null || v.llm_score != null) && (
        <div className="grid grid-cols-2 gap-3 text-xs font-mono pt-3 border-t border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-1">
              <Cpu className="w-3 h-3" strokeWidth={1.5} /> Rule engine
            </div>
            <div>{v.rule_score ?? "—"}<span className="text-muted-foreground">/100</span></div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-1">
              <Bot className="w-3 h-3" strokeWidth={1.5} /> LLM blend
            </div>
            <div>{v.llm_score == null ? <span className="text-muted-foreground">not invoked</span> : `${v.llm_score}/100`}</div>
          </div>
        </div>
      )}

      {isSender && v.rationale && (
        <div className="border-t border-border pt-3 text-[12px] text-muted-foreground flex items-start gap-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-500" strokeWidth={1.5} />
          <span data-testid="risk-rationale">{v.rationale}</span>
        </div>
      )}

      {isSender && v.signals?.length > 0 && (
        <div className="pt-3 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Signals</div>
          <div className="flex flex-wrap gap-1.5">
            {v.signals.map((s, i) => (
              <span key={i} className="px-1.5 py-0.5 border border-border bg-muted rounded-sm font-mono text-[10px]">
                {s.name}{s.value !== undefined && s.value !== true ? `=${typeof s.value === "object" ? "…" : s.value}` : ""}{s.weight ? ` · +${s.weight}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {isSender && v.compliance_recipient?.matches?.length > 0 && (
        <div className="pt-3 border-t border-border text-[12px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Sanctions match (recipient)</div>
          {v.compliance_recipient.matches.slice(0, 2).map((m, i) => (
            <div key={i} className="font-mono text-status-tampered">· {m.name} ({m.country}) — {Math.round(m.score * 100)}%</div>
          ))}
        </div>
      )}

      {!isSender && (
        <div className="text-[11px] text-muted-foreground border-t border-border pt-3">
          Detailed signals and rationale are visible only to the sender.
        </div>
      )}

      <div className="text-[10px] font-mono text-muted-foreground">
        computed {v.computed_at ? new Date(v.computed_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}
